/** Tesseract.js OCR wrapper. English-only on web. */

// CJS module + namespaced .d.ts → default import via esModuleInterop.
import Tesseract from "tesseract.js";
const { createWorker } = Tesseract;
type Worker = Tesseract.Worker;

export interface OcrTokenBox {
  /** UTF-16 code unit index into the flat OCR text, inclusive. */
  start: number;
  /** UTF-16 code unit index into the flat OCR text, exclusive. */
  endExclusive: number;
  rect: { left: number; top: number; right: number; bottom: number };
}

export interface OcrResult {
  text: string;
  tokens: OcrTokenBox[];
}

export type OcrStage =
  | { stage: "loading_worker"; progress: number }
  | { stage: "loading_lang"; progress: number }
  | { stage: "recognizing"; progress: number }
  | { stage: "done" };

export type OcrProgressListener = (s: OcrStage) => void;

let workerPromise: Promise<Worker> | null = null;

async function getWorker(listener?: OcrProgressListener): Promise<Worker> {
  if (workerPromise) return workerPromise;
  workerPromise = (async () => {
    const w = await createWorker("eng", 1, {
      logger: (m: { status?: string; progress?: number }) => {
        if (!listener || !m.status) return;
        const p = typeof m.progress === "number" ? m.progress : 0;
        if (m.status === "loading tesseract core" || m.status === "initializing tesseract") {
          listener({ stage: "loading_worker", progress: p });
        } else if (m.status.startsWith("loading language") || m.status === "initialized api") {
          listener({ stage: "loading_lang", progress: p });
        } else if (m.status === "recognizing text") {
          listener({ stage: "recognizing", progress: p });
        }
      },
    });
    return w;
  })();
  return workerPromise;
}

export async function recognize(
  image: HTMLCanvasElement | ImageBitmap | HTMLImageElement,
  listener?: OcrProgressListener,
): Promise<OcrResult> {
  const worker = await getWorker(listener);
  const canvas = toCanvas(image);
  const { data } = await worker.recognize(canvas, {}, { blocks: true });

  const parts: string[] = [];
  let pos = 0;
  const tokens: OcrTokenBox[] = [];

  const blocks = (data as unknown as { blocks?: TessBlock[] }).blocks ?? [];
  for (let bi = 0; bi < blocks.length; bi++) {
    const para = blocks[bi].paragraphs ?? [];
    for (let pi = 0; pi < para.length; pi++) {
      const lines = para[pi].lines ?? [];
      for (let li = 0; li < lines.length; li++) {
        const words = lines[li].words ?? [];
        for (let wi = 0; wi < words.length; wi++) {
          const w = words[wi];
          const t = (w.text ?? "").trim();
          if (!t) continue;
          if (parts.length > 0) {
            parts.push(" ");
            pos += 1;
          }
          const start = pos;
          parts.push(t);
          pos += t.length;
          const bb = w.bbox;
          tokens.push({
            start,
            endExclusive: pos,
            rect: { left: bb.x0, top: bb.y0, right: bb.x1, bottom: bb.y1 },
          });
        }
        parts.push("\n");
        pos += 1;
      }
    }
    parts.push("\n");
    pos += 1;
  }

  listener?.({ stage: "done" });
  return { text: parts.join(""), tokens };
}

// tesseract.js's types don't always understand ImageBitmap; normalise.
function toCanvas(img: HTMLCanvasElement | ImageBitmap | HTMLImageElement): HTMLCanvasElement {
  if (img instanceof HTMLCanvasElement) return img;
  const c = document.createElement("canvas");
  c.width = img.width;
  c.height = img.height;
  c.getContext("2d")!.drawImage(img as CanvasImageSource, 0, 0);
  return c;
}

interface TessBlock {
  paragraphs?: Array<{
    lines?: Array<{
      words?: Array<{
        text?: string;
        bbox: { x0: number; y0: number; x1: number; y1: number };
      }>;
    }>;
  }>;
}
